/**
 * AdminDashboard.tsx
 *
 * File-level:
 * - Main admin dashboard page that composes Sidebar, StatsGrid and moderation sections.
 * - Displays the new four stat cards: New Profiles, Paid Profiles, Forgot Password, Total Profiles.
 *
 * Notes:
 * - Keeps single responsibility by delegating UI pieces to small components in /components/admin.
 * - Uses the placeholder API module src/api/admin.ts when available; falls back to inline mock to avoid runtime blanking.
 */

import React, { useCallback, useEffect, useState } from 'react';
import Sidebar from '../components/admin/Sidebar';
import StatsGrid, { Stats } from '../components/admin/StatsGrid';
import SecondaryStatsGrid, { SecondaryStats } from '../components/admin/SecondaryStatsGrid';
import ThirdStatsGrid, { ThirdStats } from '../components/admin/ThirdStatsGrid';
import { UserDrawer } from '../components/admin/UserDrawer';
import AdminNewProfilesModal from '../components/admin/AdminNewProfilesModal';
import AdminTotalProfilesModal from '../components/admin/AdminTotalProfilesModal';
import AdminPaidProfilesModal from '../components/admin/AdminPaidProfilesModal';
import AdminNotPaidProfilesModal from '../components/admin/AdminNotPaidProfilesModal';
import UserManagementModal from '../components/admin/UserManagementModal';
import AdminProfileChangesModal from '../components/admin/AdminProfileChangesModal';
import AdminUpdatePhotosModal from '../components/admin/AdminUpdatePhotosModal';
import AdminUpdateDocumentsModal from '../components/admin/AdminUpdateDocumentsModal';
import AdminUpdateVideosModal from '../components/admin/AdminUpdateVideosModal';
import AdminForgotPasswordModal from '../components/AdminForgotPasswordModal';
import SubscriptionExpiresListModal from '../components/admin/SubscriptionExpiresListModal';
import FeedbackListModal from '../components/admin/FeedbackListModal';
import ViewFeedbackModal from '../components/admin/ViewFeedbackModal';
import VisitLogModal from '../components/admin/VisitLogModal';
import LoginLogModal from '../components/admin/LoginLogModal';
import useAuthStore from '@/stores/useAuthStore';

/**
 * Attempt to import admin API helpers.
 * If the module is not present in the environment, we will fallback to a local mock.
 */
let fetchAdminStats: () => Promise<Stats>;
let fetchModerationItems: () => Promise<any[]>;
let fetchForgotPasswordRequests: () => Promise<any[]>;
let fetchHomeContent: () => Promise<any>;

try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const api = require('../api/admin');
  fetchAdminStats = api.fetchAdminStats;
  fetchModerationItems = api.fetchModerationItems;
  fetchForgotPasswordRequests = api.fetchForgotPasswordRequests;
  fetchHomeContent = api.fetchHomeContent;
} catch (err) {
  // Fallback mocks so the UI works in isolation
  // eslint-disable-next-line no-console
  console.warn('[AdminDashboard] using fallback mock APIs because ../api/admin could not be imported');
  fetchAdminStats = async () =>
    new Promise((res) =>
      setTimeout(
        () =>
          res({
            newProfiles: 124,
            paidProfiles: 532,
            // Not-paid computed from total minus paid in the mock fallback
            notPaidProfiles: 8200 - 532,
            forgotPasswordCount: 2,
            totalProfiles: 8200,
            profileChanges: 45,
            updatePhotos: 78,
            updateDocuments: 23,
            updateVideos: 12,
            subscriptionExpires: 67,
            feedbackList: 0,
          }),
        150
      )
    );

  fetchModerationItems = async () =>
    new Promise((res) =>
      setTimeout(
        () =>
          res([
            { id: 'm-1', type: 'Document', title: 'Aadhaar - Front', userId: 'RJBD12345', age: '2h ago', note: 'Name matches login' },
            { id: 'm-2', type: 'Photo', title: 'Profile Photo', userId: 'RJBD54321', age: '30m ago', note: 'Watermarked' },
          ]),
        150
      )
    );

  fetchForgotPasswordRequests = async () =>
    new Promise((res) =>
      setTimeout(() => res([{ id: 'f-1', email: 'user1@example.com', requestedAt: '2025-11-28 08:12', ip: '139.59.12.1' }]), 120)
    );

  fetchHomeContent = async () =>
    new Promise((res) =>
      setTimeout(() => res({ title: 'Find your perfect match', subtitle: 'Join thousands of success stories', ctaText: 'Create Profile' }), 100)
    );
}

/**
 * AdminDashboardPage
 *
 * @description Composes the admin console layout: sidebar, stats grid, moderation queue, forgot-password list and home content editor.
 */
export default function AdminDashboardPage(): JSX.Element {
  const [activeNav, setActiveNav] = useState<'dashboard' | 'users' | 'settings'>('dashboard');
  const [stats, setStats] = useState<Stats & SecondaryStats & ThirdStats>({
    newProfiles: 0,
    paidProfiles: 0,
    notPaidProfiles: 0,
    forgotPasswordCount: 0,
    totalProfiles: 0,
    profileChanges: 0,
    updatePhotos: 0,
    updateDocuments: 0,
    updateVideos: 0,
    subscriptionExpires: 0,
    feedbackList: 0,
  });

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openNewProfilesModal, setOpenNewProfilesModal] = useState(false);
  const [openTotalProfilesModal, setOpenTotalProfilesModal] = useState(false);
  const [openPaidProfilesModal, setOpenPaidProfilesModal] = useState(false);
  const [openNotPaidProfilesModal, setOpenNotPaidProfilesModal] = useState(false);
  /** Controls visibility of the User Management modal (opened from sidebar). */
  const [openUserManagementModal, setOpenUserManagementModal] = useState(false);
  /** Controls visibility of secondary stats modals */
  const [openProfileChangesModal, setOpenProfileChangesModal] = useState(false);
  const [openUpdatePhotosModal, setOpenUpdatePhotosModal] = useState(false);
  const [openUpdateDocumentsModal, setOpenUpdateDocumentsModal] = useState(false);
  const [openUpdateVideosModal, setOpenUpdateVideosModal] = useState(false);
  const [openForgotPasswordModal, setOpenForgotPasswordModal] = useState(false);
  const [openSubscriptionExpiresModal, setOpenSubscriptionExpiresModal] = useState(false);
  const [openFeedbackListModal, setOpenFeedbackListModal] = useState(false);
  const [openViewFeedbackModal, setOpenViewFeedbackModal] = useState(false);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<number | null>(null);
  const [openVisitLogModal, setOpenVisitLogModal] = useState(false);
  const [openLoginLogModal, setOpenLoginLogModal] = useState(false);
  
  // Get logged in admin user ID
  const adminUserId = useAuthStore((s) => s.userId) || 'ADMIN';

  /**
   * loadAll
   *
   * @description Loads stats in parallel.
   */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, mod, forgot, home] = await Promise.all([fetchAdminStats()]);
      // Ensure secondary and third stats have default values if not provided by API
      setStats({
        ...s,
        profileChanges: s.profileChanges ?? 0,
        updatePhotos: s.updatePhotos ?? 0,
        updateDocuments: s.updateDocuments ?? 0,
        updateVideos: s.updateVideos ?? 0,
        subscriptionExpires: s.subscriptionExpires ?? 0,
        feedbackList: s.feedbackList ?? 0,
      });

    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AdminDashboard] failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /**
   * handleNav
   *
   * @param key string - nav key clicked in the Sidebar
   * @description updates active navigation state (router integration optional)
   */
  function handleNav(key: string) {
    // When "User Management" is clicked, open the modal with full controls.
    if (key === 'users') {
      setActiveNav('users');
      setOpenUserManagementModal(true);
      return;
    }

    // When "Visit Log" is clicked, open the VisitLogModal.
    if (key === 'visit-log') {
      setActiveNav('visit-log');
      setOpenVisitLogModal(true);
      return;
    }

    // When "Login Log" is clicked, open the LoginLogModal.
    if (key === 'login-log') {
      setActiveNav('login-log');
      setOpenLoginLogModal(true);
      return;
    }

    if (key === 'dashboard' || key === 'settings') {
      setActiveNav(key);
    }
  }

  return (
    <div className="min-h-screen" style={{ backgroundImage: 'var(--dashboard-bg)' }}>
      <div className="flex">
        <Sidebar active={activeNav} onNavigate={handleNav} />

        <div className="flex-1">
          {/* Topbar */}
          <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold">Admin Dashboard</h1>
              <div className="text-sm text-slate-500">Manage users, verification & content</div>
            </div>

            <div className="flex items-center gap-3">
              <div className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-500 p-0.5">
                <img src="https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/f1a15b83-30da-4162-a85b-99fd8315c5a1.png" className="w-9 h-9 rounded-full border-2 border-white" alt="admin"/>
              </div>
            </div>
          </header>

          <main className="p-6">
            {/* StatsGrid - top arranged cards */}
            <StatsGrid
              stats={{
                newProfiles: stats.newProfiles,
                paidProfiles: stats.paidProfiles,
                notPaidProfiles: stats.notPaidProfiles ?? 0,
                forgotPasswordCount: stats.forgotPasswordCount,
                totalProfiles: stats.totalProfiles,
              }}
              onCardClick={(key) => {
                // Small behavior: drill into users when clicking a stat
                // eslint-disable-next-line no-console
                console.log('[AdminDashboard] stat clicked', key);

                if (key === 'totalProfiles') {
                  // Open modal listing all profiles
                  setOpenTotalProfilesModal(true);
                  return;
                }

                if (key === 'newProfiles') {
                  // Open modal listing today's new profiles
                  setOpenNewProfilesModal(true);
                  return;
                }

                if (key === 'paidProfiles') {
                  // Open modal listing paid profiles
                  setOpenPaidProfilesModal(true);
                  return;
                }

                if (key === 'notPaidProfiles') {
                  // Open modal listing not-paid profiles
                  setOpenNotPaidProfilesModal(true);
                  return;
                }

                if (key === 'forgotPasswordCount') {
                  // Open forgot password requests modal
                  setOpenForgotPasswordModal(true);
                  return;
                }
              }}
            />

            {/* SecondaryStatsGrid - update statistics */}
            <SecondaryStatsGrid
              stats={{
                profileChanges: stats.profileChanges,
                updatePhotos: stats.updatePhotos,
                updateDocuments: stats.updateDocuments,
                updateVideos: stats.updateVideos,
              }}
              onCardClick={(key) => {
                // eslint-disable-next-line no-console
                console.log('[AdminDashboard] secondary stat clicked', key);
                
                if (key === 'profileChanges') {
                  setOpenProfileChangesModal(true);
                } else if (key === 'updatePhotos') {
                  setOpenUpdatePhotosModal(true);
                } else if (key === 'updateDocuments') {
                  setOpenUpdateDocumentsModal(true);
                } else if (key === 'updateVideos') {
                  setOpenUpdateVideosModal(true);
                }
              }}
            />

            {/* ThirdStatsGrid - subscription and feedback */}
            <ThirdStatsGrid
              stats={{
                subscriptionExpires: stats.subscriptionExpires,
                feedbackList: stats.feedbackList,
              }}
              onCardClick={(key) => {
                // eslint-disable-next-line no-console
                console.log('[AdminDashboard] third stat clicked', key);
                
                if (key === 'subscriptionExpires') {
                  setOpenSubscriptionExpiresModal(true);
                } else if (key === 'feedbackList') {
                  setOpenFeedbackListModal(true);
                }
              }}
            />

          </main>
        </div>

        {/* User drawer - opens when a user is selected */}
        <AdminNewProfilesModal open={openNewProfilesModal} onClose={() => setOpenNewProfilesModal(false)} />
        <AdminTotalProfilesModal open={openTotalProfilesModal} onClose={() => setOpenTotalProfilesModal(false)} />
        <AdminPaidProfilesModal open={openPaidProfilesModal} onClose={() => setOpenPaidProfilesModal(false)} />
        <AdminNotPaidProfilesModal open={openNotPaidProfilesModal} onClose={() => setOpenNotPaidProfilesModal(false)} />
        <UserManagementModal open={openUserManagementModal} onClose={() => setOpenUserManagementModal(false)} />
        
        {/* Secondary Stats Modals */}
        <AdminProfileChangesModal open={openProfileChangesModal} onClose={() => setOpenProfileChangesModal(false)} />
        <AdminUpdatePhotosModal open={openUpdatePhotosModal} onClose={() => setOpenUpdatePhotosModal(false)} />
        <AdminUpdateDocumentsModal open={openUpdateDocumentsModal} onClose={() => setOpenUpdateDocumentsModal(false)} />
        <AdminUpdateVideosModal open={openUpdateVideosModal} onClose={() => setOpenUpdateVideosModal(false)} />
        <AdminForgotPasswordModal isOpen={openForgotPasswordModal} onClose={() => setOpenForgotPasswordModal(false)} adminUserId={adminUserId} />
        
        {/* Third Stats Modals */}
        <SubscriptionExpiresListModal open={openSubscriptionExpiresModal} onClose={() => setOpenSubscriptionExpiresModal(false)} />
        <FeedbackListModal 
          open={openFeedbackListModal} 
          onClose={() => setOpenFeedbackListModal(false)}
          onViewFeedback={(id) => {
            setSelectedFeedbackId(id);
            setOpenFeedbackListModal(false);
            setOpenViewFeedbackModal(true);
          }}
        />
        <ViewFeedbackModal
          open={openViewFeedbackModal}
          onClose={() => {
            setOpenViewFeedbackModal(false);
            setSelectedFeedbackId(null);
          }}
          feedbackId={selectedFeedbackId}
        />
        <VisitLogModal open={openVisitLogModal} onClose={() => setOpenVisitLogModal(false)} />
        <LoginLogModal open={openLoginLogModal} onClose={() => setOpenLoginLogModal(false)} />
        
        <UserDrawer userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
      </div>
    </div>
  );
}
